import { useCallback, useMemo } from "react";

import useBonus from "./useBonus";

import { useUserStore } from "../contexts";

import { useUserApiService } from "../services";
import { useToast } from "./index";
import type { BonusTypes } from "../types/BonusModel";
import { Currencies } from "../types/Currency";
import type { UserProfileModel } from "../types/UserModel";

const useUser = () => {
	const userService = useUserApiService();

	const store = useUserStore();
	const { setState, setInitial } = useUserStore();

	const { listBonuses } = useBonus();

	const { showToast } = useToast();

	const methods = useMemo(() => {
		return {
			setUser(user: any) {
				setState({ user });
			},
			async getProfile() {
				try {
					const res = await userService.getProfile();
					if (res.data?.idUser) {
						setState({ profile: res.data });
					}

					return res.data;
				} catch (error) {
					console.log(error);
					setInitial();
					// if (!!error?.errors?.length) {
					// 	showToast({ text: error?.errors[0], noIcon: true });
					// }
					return null;
				}
			},
			async updateProfile(data: Partial<UserProfileModel>) {
				try {
					const res = await userService.updateProfile(data);
					if (res.data?.idUser) {
						setState({ profile: res.data });
					}

					return res.data;
				} catch (error) {
					console.log(error);
					setInitial();
					// if (!!error?.errors?.length) {
					// 	showToast({ text: error?.errors[0], noIcon: true });
					// }
					return null;
				}
			},
			async getUserInfo() {
				try {
					const res = await userService.getUserInfo();
					if (!!res.data) {
						setState({ user: res.data });
					}

					return res.data;
				} catch (error) {
					console.log(error);
					setInitial();
					// if (!!error?.errors?.length) {
					// 	showToast({ text: error?.errors[0], noIcon: true });
					// }
					return null;
				}
			},
			async listTransactions(params: { startDate?: string; endDate?: string; status?: number }) {
				try {
					setState({ loadingTransactions: true });

					const res = await userService.listTransactions(params);
					if (!!res.data) {
						setState({ transactions: res.data });
					}

					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of transactions", noIcon: true });
					return [];
				} finally {
					setState({ loadingTransactions: false });
				}
			},
			async listBonusHistory(params: { type?: BonusTypes; dateFrom?: string; dateTo?: string; event?: string }) {
				try {
					setState({ loadingBonusHistory: true });

					const bonusList = await listBonuses(params, { ignoreStateUpdate: true });
					setState({ bonusHistory: bonusList });

					return bonusList;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of bonus history", noIcon: true });
					return [];
				} finally {
					setState({ loadingBonusHistory: false });
				}
			},
			async listTournamentsHistory(params: { dateFrom?: string; dateTo?: string }) {
				try {
					setState({ loadingTournamentsHistory: true });

					const res = await userService.listTournaments(params);
					setState({ tournamentsHistory: res.data });

					return res.data || [];
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of tournaments history", noIcon: true });
					return [];
				} finally {
					setState({ loadingTournamentsHistory: false });
				}
			},
			async getDocumentTypes() {
				try {
					const res = await userService.getDocumentTypes();
					setState({ documentTypes: res.data });
					return res.data;
				} catch (error) {
					console.log(error);
					return null;
				}
			},
			async validateDocument(params: { file: any; documentType: string }) {
				try {
					setState({ validatingDocument: true });
					const res = await userService.validateDocument(params);
					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't upload file to validate document", noIcon: true });
					return null;
				} finally {
					setState({ validatingDocument: false });
				}
			},
			async checkDocumentValidations() {
				try {
					setState({ checkingDocumentValidations: true });
					const res = await userService.checkDocumentValidations();
					setState({ documentValidations: res.data || [] });
					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't check validation status of user's documents", noIcon: true });
					return [];
				} finally {
					setState({ checkingDocumentValidations: false });
				}
			},
			async updatePassword(params: { password: string; newPassword: string }) {
				try {
					const res = await userService.updatePassword(params);
					return res.data.result;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't update user's password", noIcon: true });
					return false;
				}
			},
			async getAuthenticatorSettings() {
				try {
					const res = await userService.getAuthenticatorSettings();
					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get authenticator settings", noIcon: true });
					return null;
				}
			},
			async validateAuthenticatorCode(code: string) {
				try {
					const res = await userService.validateAuthenticatorCode(code);
					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't validate authenticator code", noIcon: true });
					return null;
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [setState, userService, showToast, listBonuses, setInitial]);

	const getData = useCallback(() => {
		return {
			...store,
			isAuthorized: !!store.user,
			userCurrency: store.user?.currency || Currencies.USD
		};
	}, [store]);

	return { ...methods, getData };
};

export default useUser;
